import { createBrowserRouter } from "react-router-dom";
import {
    RootLayout,
    ContentLayout,
    AuthLayout,
    LoginLayout,
} from "@/components/layouts";
import api from "@/lib/api-client";

import RootErrorBoundary from "@/pages/errors/root-error-boundary";
import NotFound from "@/pages/errors/not-found";

import InterestForm from "@/pages/interest-form/interest-form";
import Home from "@/pages/home/index";

import Confirm from "@/pages/interest-form/confirm";

import ForClinicians from "@/pages/clinician/for-clinicians";
import Pricing from "@/pages/clinician/pricing";

import Privacy from "@/pages/legal/privacy";
import Terms from "@/pages/legal/terms";
import Governance from "@/pages/legal/governance";

import About from "@/pages/company/about";
import Contact from "@/pages/company/contact";
import HowItWorks from "@/pages/company/how-it-works";
import Specialties from "@/pages/company/specialties";

import Login from "@/pages/account/login";
import Register from "@/pages/account/register";
import ForgotPassword from "./pages/account/forgot-password";
import VerifyEmail from "./pages/account/verify-email";
import LoginDetails from "@/pages/account/change-details/index";
import EmailDetails from "@/pages/account/change-details/email";
import PasswordDetails from "@/pages/account/change-details/password";
import MfaDetails from "@/pages/account/change-details/mfa";

import Dashboard from "@/pages/clinician/dashboard";
import Manage from "@/pages/clinician/manage";
import Services from "@/pages/clinician/services/index";
import EditServices from "@/pages/clinician/services/edit";
import CreateServices from "@/pages/clinician/services/create";
import ClinicianAvailability from "@/pages/clinician/availability";
import ClinicianAppointments from "@/pages/clinician/appointments/index";

import PatientManage from "@/pages/patient/manage";
import PatientAppoinments from "@/pages/patient/appointments/index";
import PatientEditAppointment from "@/pages/patient/appointments/edit";
import PatientFindAppointments from "@/pages/patient/appointments/find";
import PatientViewClinicians from "@/pages/patient/appointments/view";
import PatientBookClinicians from "@/pages/patient/appointments/book";

const subdomain = window.location.hostname.split(".")[0];

export const router = createBrowserRouter(
    subdomain === "www"
        ? [
              {
                  Component: RootLayout,
                  children: [
                      {
                          Component: ContentLayout,
                          ErrorBoundary: RootErrorBoundary,
                          children: [
                              {
                                  path: "/",
                                  Component: InterestForm,
                              },
                              {
                                  path: "/confirm",
                                  loader: async ({ request }) => {
                                      try {
                                          const url = new URL(request.url);
                                          const id = url.searchParams.get("id");

                                          await api.post(
                                              "/InterestForm/Confirm",
                                              JSON.stringify(id),
                                              {
                                                  headers: {
                                                      "Content-Type":
                                                          "application/json",
                                                  },
                                              },
                                          );

                                          return { success: true };
                                      } catch {
                                          return { success: false };
                                      }
                                  },
                                  Component: Confirm,
                              },
                              {
                                  path: "/privacy",
                                  Component: Privacy,
                              },
                              {
                                  path: "/about",
                                  Component: About,
                              },
                              {
                                  path: "*",
                                  Component: NotFound,
                              },
                          ],
                      },
                  ],
              },
          ]
        : [
              {
                  Component: RootLayout,
                  children: [
                      {
                          Component: ContentLayout,
                          ErrorBoundary: RootErrorBoundary,
                          children: [
                              {
                                  path: "/",
                                  Component: Home,
                              },
                              {
                                  path: "/for-clinicians",
                                  Component: ForClinicians,
                              },
                              {
                                  path: "/pricing",
                                  Component: Pricing,
                              },
                              {
                                  path: "/privacy",
                                  Component: Privacy,
                              },
                              {
                                  path: "/specialties",
                                  Component: Specialties,
                              },
                              {
                                  path: "/terms",
                                  Component: Terms,
                              },
                              {
                                  path: "/governance",
                                  Component: Governance,
                              },
                              {
                                  path: "/about",
                                  Component: About,
                              },
                              {
                                  path: "/how-it-works",
                                  Component: HowItWorks,
                              },
                              {
                                  path: "/contact",
                                  Component: Contact,
                              },
                              {
                                  path: "/clinicians/find",
                                  Component: PatientFindAppointments,
                              },
                              {
                                  path: "/clinicians/view/:name/:id",
                                  Component: PatientViewClinicians,
                              },
                              {
                                  path: "/clinicians/book",
                                  Component: PatientBookClinicians,
                              },
                              {
                                  path: "*",
                                  Component: NotFound,
                              },
                          ],
                      },
                      {
                          Component: LoginLayout,
                          ErrorBoundary: RootErrorBoundary,
                          children: [
                              {
                                  path: "/login",
                                  Component: Login,
                              },
                              {
                                  path: "/register",
                                  Component: Register,
                              },
                              {
                                  path: "/forgot-password",
                                  Component: ForgotPassword,
                              },
                              {
                                  path: "/verify-email",
                                  Component: VerifyEmail,
                              },
                          ],
                      },
                      {
                          Component: AuthLayout,
                          ErrorBoundary: RootErrorBoundary,
                          children: [
                              {
                                  children: [
                                      {
                                          path: "/clinician",
                                          Component: Dashboard,
                                      },
                                      {
                                          path: "/clinician/manage",
                                          Component: Manage,
                                      },
                                      {
                                          path: "/clinician/manage/account",
                                          Component: LoginDetails,
                                      },
                                      {
                                          path: "/clinician/manage/account/email",
                                          Component: EmailDetails,
                                      },
                                      {
                                          path: "/clinician/manage/account/password",
                                          Component: PasswordDetails,
                                      },
                                      {
                                          path: "/clinician/manage/account/mfa",
                                          Component: MfaDetails,
                                      },
                                      {
                                          path: "/clinician/services",
                                          Component: Services,
                                      },
                                      {
                                          path: "/clinician/services/new",
                                          Component: CreateServices,
                                      },
                                      {
                                          path: "/clinician/services/edit/:serviceGuid",
                                          Component: EditServices,
                                      },
                                      {
                                          path: "/clinician/availability",
                                          Component: ClinicianAvailability,
                                      },
                                      {
                                          path: "/clinician/appointments",
                                          Component: ClinicianAppointments,
                                      },
                                  ],
                              },
                              {
                                  children: [
                                      {
                                          path: "/patient/manage",
                                          Component: PatientManage,
                                      },
                                      {
                                          path: "/patient/manage/account",
                                          Component: LoginDetails,
                                      },

                                      {
                                          path: "/patient/manage/account/email",
                                          Component: EmailDetails,
                                      },
                                      {
                                          path: "/patient/manage/account/password",
                                          Component: PasswordDetails,
                                      },
                                      {
                                          path: "/patient/manage/account/mfa",
                                          Component: MfaDetails,
                                      },
                                      {
                                          path: "/patient/appointments",
                                          Component: PatientAppoinments,
                                      },
                                      {
                                          path: "/patient/appointments/edit/:id",
                                          Component: PatientEditAppointment,
                                      },
                                  ],
                              },
                          ],
                      },
                  ],
              },
          ],
);
